/**
 * JobAssignDialog — escolhe nerd pra atribuir manualmente um job.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Star, UserCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { nerdsApi } from "@/lib/api-cadastros";
import { jobActionsApi } from "@/lib/api-bulk";
import { cn } from "@/lib/utils";

interface JobAssignDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function JobAssignDialog({ jobId, open, onOpenChange }: JobAssignDialogProps) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const nerds = useQuery({
    queryKey: ["nerds-for-assign"],
    queryFn: () => nerdsApi.list({ status: "approved", size: 200 }),
    enabled: open,
  });

  const assign = useMutation({
    mutationFn: () => jobActionsApi.assignNerd(jobId, selectedId!, note || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success("Técnico atribuído");
      onOpenChange(false);
      setSelectedId(null);
      setNote("");
      setQ("");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const filtered = useMemo(() => {
    const items = nerds.data?.items ?? [];
    if (!q) return items.slice(0, 50);
    const lower = q.toLowerCase();
    return items.filter(
      (n) =>
        (n.legal_name ?? "").toLowerCase().includes(lower) ||
        (n.cpf ?? "").includes(lower) ||
        (n.cnpj ?? "").includes(lower),
    ).slice(0, 50);
  }, [nerds.data, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Atribuir técnico</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nome, CPF/CNPJ..."
              className="pl-9"
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto -mx-2 space-y-1">
            {nerds.isLoading ? (
              <p className="text-sm text-muted-foreground p-4">Carregando…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">Nenhum técnico encontrado.</p>
            ) : (
              filtered.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setSelectedId(n.id)}
                  className={cn(
                    "w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors",
                    selectedId === n.id
                      ? "bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-200 dark:ring-primary-500/30"
                      : "hover:bg-slate-50 dark:hover:bg-white/[0.03]",
                  )}
                >
                  <Avatar size="md">
                    <AvatarFallback name={n.legal_name ?? ""} />
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{n.legal_name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
                      <span className="inline-flex items-center gap-1">
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                        {n.rank_score}/100
                      </span>
                      <span>{n.completed_jobs} jobs</span>
                      {n.address?.cidade && (
                        <span>{n.address.cidade.name}/{n.address.cidade.uf}</span>
                      )}
                    </p>
                  </div>
                  <Badge variant="muted" size="sm">{n.working_radius_km} km</Badge>
                </button>
              ))
            )}
          </div>

          <div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Observação interna (opcional)"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200/60 dark:border-white/10">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={() => assign.mutate()}
              disabled={!selectedId || assign.isPending}
            >
              <UserCheck className="h-4 w-4" />
              {assign.isPending ? "Atribuindo..." : "Atribuir"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
